import { updateRoadMap } from '@/domain/roadmap/api';
import { type RoadMapUpdate, type StatusResource } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { patchRoadmapListCaches } from '@/features/roadmap/utils';
import { validatePlannedHours } from '@/features/roadmap/utils/planningHours';
import { useAuth } from '@/hooks/useAuth';
import { type ApiError } from '@/services/ApiError';
import { getRandomId } from '@/utils/number';
import { canEditRoadmap, isRoadmapParticipant } from '@/utils/roadmapPermission';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { type OverviewFormHandle } from '../components/MainPage/UpdateItem/OverviewRoadmapTabContent';
import { useEditableRoadmapById } from './useEditableRoadmapById';

// For each edited stage, include previously-saved resources that the user deleted
// with hours: 0 so the server (which does UPSERT) zeroes them out instead of
// keeping stale data that would inflate the total.
function zeroOutDeletedResources(item: RoadMapUpdate, original: RoadMapUpdate | undefined): RoadMapUpdate {
  if (!item.type?.roadmapStatus || !original?.type?.roadmapStatus) return item;
  const originalByStageId = new Map(original.type.roadmapStatus.map((s) => [s.idRoadmapStatus, s.resources ?? []]));
  const patchedStatuses = item.type.roadmapStatus.map((stage) => {
    if (!stage.edited) return stage;
    const originalResources = originalByStageId.get(stage.idRoadmapStatus) ?? [];
    const currentIds = new Set((stage.resources ?? []).map((r) => r.idRoadmapStatusResource));
    const zeroed = originalResources
      .filter(
        (r) => r.idResource === null && r.idRoadmapStatusResource > 0 && !currentIds.has(r.idRoadmapStatusResource),
      )
      .map((r) => ({ ...r, hours: 0 }));
    if (zeroed.length === 0) return stage;
    return { ...stage, resources: [...zeroed, ...(stage.resources ?? [])] };
  });
  return { ...item, type: { ...item.type, roadmapStatus: patchedStatuses } };
}

export function useRoadmapItemEditor(idRoadmap: number | null) {
  const overviewRef = useRef<OverviewFormHandle | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);

  const { data: roadmap, isLoading, isError } = useEditableRoadmapById(idRoadmap);

  // Always capture the latest server snapshot so handleSave can detect deletions.
  const roadmapRef = useRef(roadmap);
  useEffect(() => {
    roadmapRef.current = roadmap;
  }, [roadmap]);

  const storeRoadmapId = useRoadmapEditStore((s) => s.editableRoadMap.idRoadmap);
  const isStoreStale = !!idRoadmap && storeRoadmapId !== idRoadmap;

  useEffect(() => {
    if (roadmap?.idRoadmap !== idRoadmap) return;
    useRoadmapEditStore.setState({
      editableRoadMap: roadmap,
      isStoreDirty: false,
      isPlannedHoursDirty: false,
      plannedHoursRows: [],
    });
  }, [roadmap, idRoadmap]);

  const viewOnly = useMemo(() => {
    if (!roadmap || !user) return true;
    return !canEditRoadmap(user, roadmap);
  }, [roadmap, user]);

  const showSaveCancel = !viewOnly || (!!roadmap && isRoadmapParticipant(user, roadmap));

  const hasUnsavedChanges = useCallback(() => {
    return useRoadmapEditStore.getState().isStoreDirty || (overviewRef.current?.isDirty() ?? false);
  }, []);

  const cancelForm = useCallback(() => {
    overviewRef.current?.cancel();
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: updateRoadMap,
    onSuccess: (saved: RoadMapUpdate) => {
      toast.dismiss();
      patchRoadmapListCaches(queryClient, saved);
      useRoadmapEditStore.setState({ isStoreDirty: false, isPlannedHoursDirty: false });
      toast.success(`${saved.item} has been updated!`);
      queryClient.invalidateQueries({ queryKey: ['roadmap', 'editable', saved.idRoadmap] }).catch(() => null);
    },
    onError: (error: ApiError) => {
      toast.error(`An error occurred while saving! ${error.message}`, { position: 'top-right', autoClose: false });
    },
  });

  const handleSave = useCallback(async () => {
    if (!overviewRef.current) return;
    setIsUpdatingItem(true);
    try {
      const item = await overviewRef.current.getValidatedData();
      if (!item) return;
      const [, epicTicket] = await Promise.all([
        overviewRef.current.saveFollowers(),
        overviewRef.current.createJiraEpicIfNeeded(item.item, item.description, item.idRoadmap),
      ]);
      let finalItem = epicTicket ? { ...item, jiraTicket: epicTicket } : item;

      const { plannedHoursRows, editableRoadMap: storeRoadmap, isPlannedHoursDirty } = useRoadmapEditStore.getState();
      if (plannedHoursRows.length > 0 && isPlannedHoursDirty) {
        const plannedHoursVal = storeRoadmap.plannedHours ?? 0;
        const totalHours = plannedHoursRows.reduce((sum, r) => sum + r.hours, 0);
        const validation = validatePlannedHours(plannedHoursRows.length, totalHours, plannedHoursVal);
        if (validation) {
          if (validation.level === 'error') {
            toast.error(validation.message, { position: 'top-right', autoClose: false });
            return;
          } else {
            toast.warn(validation.message, { position: 'top-right', autoClose: false });
          }
        }
      }
      // Only update resources when the user explicitly changed activities in the
      // Business tab. Skipping this block prevents phantom hour increases when
      // the user only edits Planning statuses (the Business tab mounts in the
      // background and always populates plannedHoursRows from server data).
      if (plannedHoursRows.length > 0 && isPlannedHoursDirty && storeRoadmap.type?.roadmapStatus?.length) {
        const [first, ...rest] = storeRoadmap.type.roadmapStatus;

        // Use the server snapshot as the source of truth for existing resources.
        // Using storeRoadmap here would cause duplicates on consecutive saves
        // because the store accumulates resources between refetches.
        const serverFirst = roadmapRef.current?.type?.roadmapStatus?.[0];
        const existingResources = serverFirst?.resources ?? [];

        // Only match against activity resources (idResource === null) so we
        // never accidentally overwrite Cost-tab user-assigned entries.
        const activityByResource = new Map<number, StatusResource[]>();
        for (const r of existingResources) {
          if (r.idResource !== null) continue;
          const key = r.idRoadmapResource ?? -1;
          const arr = activityByResource.get(key) ?? [];
          arr.push(r);
          activityByResource.set(key, arr);
        }

        const usedIds = new Set<number>();
        const updatedFromPlanned: StatusResource[] = plannedHoursRows.map((row) => {
          const candidates = activityByResource.get(row.id) ?? [];
          const existing = candidates.find((r) => !usedIds.has(r.idRoadmapStatusResource));
          if (existing) {
            usedIds.add(existing.idRoadmapStatusResource);
            return { ...existing, hours: row.hours };
          }
          return {
            idRoadmapStatusResource: -getRandomId(),
            idRoadmap: finalItem.idRoadmap,
            idRoadmapResource: row.id,
            resource: row.resource,
            alias: row.alias,
            hours: row.hours,
            idResource: null,
          };
        });

        // Keep only user-assigned resources (Cost tab). Activity resources
        // not present in plannedHoursRows are intentionally excluded —
        // zeroOutDeletedResources will zero them on the server.
        const preserved = (first.resources ?? []).filter((r) => r.idResource !== null);

        // Zero out activity resources that existed before but are no longer in plannedHoursRows.
        const plannedIds = new Set(plannedHoursRows.map((r) => r.id));
        const zeroed = existingResources
          .filter(
            (r) => r.idResource === null && r.idRoadmapStatusResource > 0 && !plannedIds.has(r.idRoadmapResource ?? -1),
          )
          .map((r) => ({ ...r, hours: 0 }));

        const updatedFirst = { ...first, resources: [...preserved, ...updatedFromPlanned, ...zeroed], edited: true };
        const filteredStatuses = [updatedFirst, ...rest].filter(
          (s) => s.edited || s.roadmapStatusItems.some((si) => si.edited),
        );
        finalItem = {
          ...finalItem,
          type: finalItem.type ? { ...finalItem.type, roadmapStatus: filteredStatuses } : null,
        };
      }

      overviewRef.current.resetForm(finalItem);
      mutate(zeroOutDeletedResources(finalItem, roadmapRef.current));
    } finally {
      setIsUpdatingItem(false);
    }
  }, [mutate]);

  return {
    roadmap,
    isLoading: isLoading || isStoreStale,
    isError,
    viewOnly,
    showSaveCancel,
    hasUnsavedChanges,
    cancelForm,
    handleSave,
    isPending: isPending || isUpdatingItem,
    overviewRef,
  };
}
